const { camelToTitleCase } = require("@bloglovin/vue-framework");

// Entries are `state:breakpoint:optionKey`, per DynamicOption.collectOverriddenOptionKeys.
// optionKey may itself contain ":", so only the first two segments are split off.
function parseOverrideComposite(composite) {
  const parts = String(composite).split(":");
  if (parts.length < 3) {
    return null;
  }
  const [state, breakpoint, ...rest] = parts;
  return { state, breakpoint, optionKey: rest.join(":") };
}

// Overrides recorded at one breakpoint, across every state. The layers panel is
// scoped to the breakpoint being viewed, so Phone overrides stay invisible on Desktop.
function overridesAtBreakpoint(overriddenOptionKeysSet, breakpointAlias) {
  const alias = breakpointAlias || "default";
  const seen = new Set();
  const overrides = [];
  for (const composite of overriddenOptionKeysSet ?? []) {
    const parsed = parseOverrideComposite(composite);
    if (!parsed || parsed.breakpoint !== alias) {
      continue;
    }
    const dedupeKey = `${parsed.state}:${parsed.optionKey}`;
    if (seen.has(dedupeKey)) {
      continue;
    }
    seen.add(dedupeKey);
    overrides.push({ state: parsed.state, optionKey: parsed.optionKey });
  }
  return overrides.sort((a, b) => `${a.optionKey}${a.state}`.localeCompare(`${b.optionKey}${b.state}`));
}

// At `default:default` there is no broader coord to override, only branding — so a
// value counts only where the chain supplies one: buttons and typography, not containers.
function isExplicitLeaf(leaf) {
  if (!leaf || typeof leaf !== "object") {
    return false;
  }
  return leaf.r !== undefined && leaf.i !== true;
}

function explicitDefaultCoordOverrides(parsedOptions, hasBrandingFallback = () => false) {
  const modeTrees = Object.values(parsedOptions ?? {});
  const seen = new Set();
  const overrides = [];
  for (const modeTree of modeTrees) {
    const baseCoordLeaves = Object.entries(modeTree?.default?.default ?? {});
    for (const [optionKey, leaf] of baseCoordLeaves) {
      const alreadyFound = seen.has(optionKey);
      if (alreadyFound || !isExplicitLeaf(leaf) || !hasBrandingFallback(optionKey)) {
        continue;
      }
      seen.add(optionKey);
      overrides.push({ state: "default", optionKey });
    }
  }
  return overrides;
}

function overridesForBreakpoint({
  overriddenOptionKeysSet,
  parsedOptions,
  breakpointAlias,
  hasBrandingFallback,
}) {
  const alias = breakpointAlias || "default";
  const overrides = overridesAtBreakpoint(overriddenOptionKeysSet, alias);
  if (alias !== "default") {
    return overrides;
  }
  const seen = new Set(overrides.map(override => `${override.state}:${override.optionKey}`));
  for (const override of explicitDefaultCoordOverrides(parsedOptions, hasBrandingFallback)) {
    const dedupeKey = `${override.state}:${override.optionKey}`;
    if (!seen.has(dedupeKey)) {
      seen.add(dedupeKey);
      overrides.push(override);
    }
  }
  return overrides.sort((a, b) => `${a.optionKey}${a.state}`.localeCompare(`${b.optionKey}${b.state}`));
}

// Overrides on this element across EVERY breakpoint, grouped by breakpoint.
// Unlike overridesForBreakpoint (scoped to one alias for the focused view), this
// ignores focus so the layers icon shows if any breakpoint overrides an option.
function overridesByBreakpoint({
  overriddenOptionKeysSet,
  parsedOptions,
  breakpoints,
  hasBrandingFallback,
}) {
  const groups = [];
  for (const breakpoint of breakpoints ?? []) {
    const overrides = overridesForBreakpoint({
      overriddenOptionKeysSet,
      parsedOptions,
      breakpointAlias: breakpoint.alias,
      hasBrandingFallback,
    });
    if (overrides.length) {
      groups.push({ alias: breakpoint.alias, label: breakpoint.label, overrides });
    }
  }
  return groups;
}

function describeOverride({ state, optionKey }) {
  const label = camelToTitleCase(String(optionKey).replace(/-/g, " "));
  if (!state || state === "default") {
    return label;
  }
  return `${label} (${state})`;
}

module.exports = {
  parseOverrideComposite,
  overridesAtBreakpoint,
  explicitDefaultCoordOverrides,
  overridesForBreakpoint,
  overridesByBreakpoint,
  describeOverride,
};
